const DECIMAL_PATTERN = /^(-?)(\d+)(?:[.,](\d+))?$/;

const parseDecimal = (value) => {
  const match = String(value ?? '').trim().match(DECIMAL_PATTERN);
  if (!match) throw new TypeError('Invalid decimal value: ' + value);
  const fraction = match[3] || '';
  return {
    units: BigInt(match[1] + match[2] + fraction),
    scale: fraction.length,
  };
};

const powerOfTen = (scale) => 10n ** BigInt(scale);

const roundToScale = ({ units, scale }, targetScale) => {
  if (scale <= targetScale) return units * powerOfTen(targetScale - scale);
  const divisor = powerOfTen(scale - targetScale);
  const sign = units < 0n ? -1n : 1n;
  const absolute = units < 0n ? -units : units;
  let rounded = absolute / divisor;
  if ((absolute % divisor) * 2n >= divisor) rounded += 1n;
  return rounded * sign;
};

const formatFixed = (units, scale = 2) => {
  const sign = units < 0n ? '-' : '';
  const absolute = units < 0n ? -units : units;
  const raw = absolute.toString().padStart(scale + 1, '0');
  return sign + (raw.slice(0, -scale) || '0') + '.' + raw.slice(-scale);
};

export const formatQuantity = (value) => {
  const parsed = parseDecimal(value);
  if (parsed.units === 0n) return '0';
  const sign = parsed.units < 0n ? '-' : '';
  const absolute = parsed.units < 0n ? -parsed.units : parsed.units;
  const raw = absolute.toString().padStart(parsed.scale + 1, '0');
  if (parsed.scale === 0) return sign + raw;
  const whole = raw.slice(0, -parsed.scale) || '0';
  const fraction = raw.slice(-parsed.scale).replace(/0+$/, '');
  return fraction ? sign + whole + ',' + fraction : sign + whole;
};

export const formatMoney = (value) => {
  const cents = roundToScale(parseDecimal(value), 2);
  return formatFixed(cents, 2).replace('.', ',') + ' $';
};

export const calculateSubtotal = (quantity, salePrice) => {
  const left = parseDecimal(quantity);
  const right = parseDecimal(salePrice);
  return formatFixed(roundToScale({
    units: left.units * right.units,
    scale: left.scale + right.scale,
  }, 2), 2);
};

export const calculateMargin = (salePrice, purchasePrice) => {
  const sale = parseDecimal(salePrice);
  const purchase = parseDecimal(purchasePrice);
  const scale = Math.max(sale.scale, purchase.scale);
  return formatFixed(roundToScale({
    units: sale.units * powerOfTen(scale - sale.scale) -
      purchase.units * powerOfTen(scale - purchase.scale),
    scale,
  }, 2), 2);
};

export const sumMoney = (values) => formatFixed(
  values.reduce((total, value) => total + roundToScale(parseDecimal(value), 2), 0n),
  2
);

export const isLowStock = (quantity, minimum) => {
  const stock = parseDecimal(quantity);
  const threshold = parseDecimal(minimum);
  const scale = Math.max(stock.scale, threshold.scale);
  return stock.units * powerOfTen(scale - stock.scale) <=
    threshold.units * powerOfTen(scale - threshold.scale);
};

export const can = (user, permission) => {
  const permissions = user?.permissions || [];
  return permissions.includes('*') || permissions.includes(permission);
};
